<script setup>
import { computed, ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'


const props = defineProps({
  projet:                  { type: Object,  default: () => ({}) },
  estimation:              { type: Object,  default: () => ({}) },
  roles_utilisateurs:      { type: Array,   default: () => [] },
  activer_fonction:        { type: Boolean, default: false },
  personnels_par_fonction: { type: Array,   default: () => [] },
  personnels:              { type: Array,   default: () => [] },
  show_prenom:             { type: Boolean, default: false },
  show_type_fonction:      { type: Boolean, default: false },
  show_description_perso:  { type: Boolean, default: false },
  type_fonctions:          { type: Array,   default: () => [] },
  activer_type_equipement: { type: Boolean, default: false },
  equipements_par_type:    { type: Array,   default: () => [] },
  equipements:             { type: Array,   default: () => [] },
  show_type_equipement:    { type: Boolean, default: false },
  show_description_equip:  { type: Boolean, default: false },
  type_equipements:        { type: Array,   default: () => [] },
  inventaire_enabled:      { type: Boolean, default: false },
  inventaires:             { type: Array,   default: () => [] },
  show_type_taux:          { type: Boolean, default: false },
  type_taux:               { type: Array,   default: () => [] },
  type_unites:             { type: Array,   default: () => [] },
  type_parties:            { type: Array,   default: () => [] },
  can_see_costs:           { type: Boolean, default: false },
  type_partie_suivies:     { type: Array,   default: () => [] },
  section:                 { type: String,  default: '' },
  mod_detail:              { type: Boolean, default: false },
  estimation_personnels:   { type: Array,   default: () => [] },
  estimation_equipements:  { type: Array,   default: () => [] },
  errors:                  { type: Object,  default: () => ({}) },
  projet_url:              { type: String,  default: '/projets' },
  estimation_url:          { type: String,  default: '/estimations' },
  update_url:              { type: String,  default: '/estimations' },
})

// ── Filtrage par section ─────────────────────────────────────
function showSection(name) {
  return !props.section || props.section === name
}

// ── Classes utilitaires ──────────────────────────────────────
const inputCls        = 'w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all'
const inputSmCls      = 'w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30'
const sectionHeaderCls = 'px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between'
const sectionTitleCls  = 'text-[11px] font-black uppercase tracking-widest text-outline'
const labelCls         = 'block text-[10px] font-black uppercase tracking-widest text-outline mb-1.5'
const addBtnCls        = 'flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-primary hover:brightness-125 transition-all'
const removeBtnCls     = 'text-outline hover:text-error transition-colors'

// ── Formulaire pré-rempli ─────────────────────────────────────
const est = props.estimation

const form = useForm({
  estimation: {
    projet_id:         props.projet.id,
    nom:               est.nom ?? '',
    description:       est.description ?? '',
    date_soumission:   est.date_soumission ?? '',
    date_acceptation:  est.date_acceptation ?? '',
    utilisateur_ids:   [...(est.utilisateur_ids ?? [])],
    personnel_ids:     [...(est.personnel_ids ?? [])],
    equipement_ids:    [...(est.equipement_ids ?? [])],
    estimation_element_tags_attributes:
      (est.estimation_element_tags_attributes ?? []).map(t => ({ ...t })),
    estimation_elements_attributes:
      (est.estimation_elements_attributes ?? []).map(el => ({
        ...el,
        estimation_parties_attributes: (el.estimation_parties_attributes ?? []).map(p => ({ ...p })),
      })),
    estimation_inventaires_attributes:
      (est.estimation_inventaires_attributes ?? []).map(i => ({ ...i })),
    estimation_partie_suivies_attributes:
      (est.estimation_partie_suivies_attributes ?? []).map(p => ({ ...p })),
    personnel_externes_attributes:
      (est.personnel_externes_attributes ?? []).map(p => ({ ...p })),
    equipement_externes_attributes:
      (est.equipement_externes_attributes ?? []).map(e => ({ ...e })),
  },
})

// ── Erreurs ──────────────────────────────────────────────────
const allErrors = computed(() => ({ ...form.errors, ...props.errors }))
function err(f) { return allErrors.value[f] }

const { error: toastError } = useToast()
watch(allErrors, (errs) => {
  const msgs = Object.entries(errs).map(([f, m]) => `${f} : ${Array.isArray(m) ? m.join(', ') : m}`)
  if (msgs.length) toastError(msgs.join('\n'))
}, { deep: true })

// ── Soumission ────────────────────────────────────────────────
function submit() {
  form.transform(data => {
    const payload = { estimation: { ...data.estimation } }
    if (props.mod_detail && props.section === 'personnel') {
      payload.estimation.estimation_personnels_attributes = formDetailPersonnels.value
    }
    if (props.mod_detail && props.section === 'equipements') {
      payload.estimation.estimation_equipements_attributes = formDetailEquipements.value
    }
    return payload
  }).patch(props.update_url, { preserveScroll: true })
}

// ── Helpers add/remove (avec _destroy pour lignes existantes) ─
// ── Formulaire détail personnel/equipement (mod_detail) ──────
const formDetailPersonnels = ref(props.estimation_personnels.map(ep => ({ ...ep })))
const formDetailEquipements = ref(props.estimation_equipements.map(ee => ({ ...ee })))

function addEstimationPersonnel()  { formDetailPersonnels.value.push({ personnel_id: '', taux_horaire: '', _destroy: false }) }
function addEstimationEquipement() { formDetailEquipements.value.push({ equipement_id: '', taux_horaire: '', _destroy: false }) }
function removeDetailRow(arr, i) {
  if (arr[i].id) { arr[i]._destroy = true } else { arr.splice(i, 1) }
}

function addPersonnelExterne()  { form.estimation.personnel_externes_attributes.push({ code: '', nom: '', prenom: '', type_fonction_id: '', description: '', taux_horaire: '', actif: true, _destroy: false }) }
function addEquipementExterne() { form.estimation.equipement_externes_attributes.push({ code: '', nom: '', type_equipement_id: '', description: '', taux_horaire: '', actif: true, _destroy: false }) }
function addInventaire()        { form.estimation.estimation_inventaires_attributes.push({ inventaire_id: '', taux: '', type_taux_id: '', _destroy: false }) }
function addTag()               { form.estimation.estimation_element_tags_attributes.push({ tag: '', ordre: '', _destroy: false }) }
function addElement() {
  form.estimation.estimation_elements_attributes.push({
    code: '', description: '', quantite: '', type_unite_id: '', estimation_element_tag_id: '',
    _destroy: false,
    estimation_parties_attributes: props.type_parties.map(tp => ({ type_partie_estimation_id: tp.id, prix_coutant: '' })),
  })
}
function addPartieSuivie() { form.estimation.estimation_partie_suivies_attributes.push({ type_partie_estimation_id: '', _destroy: false }) }

// Suppression : _destroy si id existant, sinon splice
function removeRow(arr, i) {
  if (arr[i].id) {
    arr[i]._destroy = true
  } else {
    arr.splice(i, 1)
  }
}

// ── Tags disponibles pour le select des items ─────────────────
const tagsDisponibles = computed(() =>
  form.estimation.estimation_element_tags_attributes.filter(t => !t._destroy && t.tag)
)

// ── Bouton ↑ : visible après 300px de scroll ─────────────────
// Le scroll est sur <main> dans AppLayout (overflow-y-auto), pas sur window
const showScrollTop = ref(false)
let scrollEl = null
function onScroll() { showScrollTop.value = (scrollEl?.scrollTop ?? 0) > 300 }

// ── Masquage bouton flottant quand le bouton bas est visible ─
const bottomSaveBtn = ref(null)
const bottomSaveInView = ref(false)
let bottomSaveObserver = null

onMounted(() => {
  scrollEl = document.querySelector('main')
  scrollEl?.addEventListener('scroll', onScroll)

  // IntersectionObserver pour masquer le bouton flottant
  // quand le bouton "Enregistrer" bas du formulaire entre dans le viewport
  if (bottomSaveBtn.value && scrollEl) {
    bottomSaveObserver = new IntersectionObserver(
      ([entry]) => { bottomSaveInView.value = entry.isIntersecting },
      { root: scrollEl, threshold: 0.1 }
    )
    bottomSaveObserver.observe(bottomSaveBtn.value)
  }

  // Scroll vers la section ciblée par le param section
  if (props.section) {
    setTimeout(() => {
      const el = document.getElementById(`section-${props.section}`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 150)
  }
})
onUnmounted(() => {
  scrollEl?.removeEventListener('scroll', onScroll)
  bottomSaveObserver?.disconnect()
})
function scrollToTop() {
  document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' })
}

// ── Popover description ───────────────────────────────────────
const expandedDescIndex = ref(null)
const expandedDescValue = ref('')
const descTextarea = ref(null)

function openDescPopover(i, value) {
  expandedDescIndex.value = i
  expandedDescValue.value = value ?? ''
  // Le <textarea> est dans un v-for → on attend 2 frames (mount + layout)
  // puis on focus via une ref fonction qui capture l'élément réel.
  nextTick(() => {
    requestAnimationFrame(() => {
      const el = descTextarea.value
      if (!el) return
      el.focus()
      if (typeof el.setSelectionRange === 'function') {
        const len = el.value.length
        el.setSelectionRange(len, len)
      }
    })
  })
}

// Ref fonction : Vue appelle avec l'élément au mount et null au démontage
function setDescTextareaRef(el) {
  descTextarea.value = el || null
}
function cancelDescPopover() {
  expandedDescIndex.value = null
  expandedDescValue.value = ''
}
function validateDescPopover(el) {
  el.description = expandedDescValue.value
  expandedDescIndex.value = null
  expandedDescValue.value = ''
}

</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="estimation_url" class="text-outline hover:text-on-surface transition-colors">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <a :href="projet_url" class="font-mono text-sm font-bold text-primary hover:brightness-125">{{ projet.code }}</a>
        <span class="text-outline">/</span>
        <a :href="estimation_url" class="font-mono text-sm font-bold text-primary hover:brightness-125">{{ estimation.nom }}</a>
        <span class="text-outline">/</span>
        <span class="text-sm font-black uppercase tracking-widest text-on-surface">Modifier</span>
      </div>
    </template>

    <div class="p-8">
      <div class="space-y-6">

        <!-- En-tête -->
        <div>
          <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase">Modifier le bordereau</h1>
          <p class="text-sm text-outline mt-1">
            Projet <span class="font-mono font-bold text-primary">{{ projet.code }}</span> — {{ projet.nom }}
          </p>
        </div>

        <form @submit.prevent="submit" class="space-y-6">

          <!-- ── 1. Détails ── -->
          <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHeaderCls">
              <h2 :class="sectionTitleCls">Détails</h2>
            </div>
            <div class="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label :class="labelCls">Projet</label>
                <div class="px-3 py-2 rounded-md bg-surface-container-highest text-sm font-mono font-bold text-primary">
                  {{ projet.code }} — {{ projet.nom }}
                </div>
              </div>
              <div>
                <label :class="labelCls">Nom <span class="text-error">*</span></label>
                <input v-model="form.estimation.nom" type="text"
                       :class="[inputCls, err('nom') ? 'ring-2 ring-error/40' : '']" />
                <p v-if="err('nom')" class="text-xs text-error mt-1">{{ err('nom') }}</p>
              </div>
              <div>
                <label :class="labelCls">Date de soumission</label>
                <input v-model="form.estimation.date_soumission" type="date" :class="inputCls" />
              </div>
              <div>
                <label :class="labelCls">Date d'acceptation</label>
                <input v-model="form.estimation.date_acceptation" type="date" :class="inputCls" />
              </div>
              <div class="sm:col-span-2">
                <label :class="labelCls">Description</label>
                <textarea v-model="form.estimation.description" rows="3" :class="inputCls + ' resize-none'"></textarea>
              </div>
            </div>
          </div>

          <!-- ── 2. Utilisateurs ── -->
          <div v-if="showSection('utilisateurs') && roles_utilisateurs.length > 0"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHeaderCls">
              <h2 :class="sectionTitleCls">Utilisateurs assignés</h2>
            </div>
            <div class="p-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              <div v-for="role in roles_utilisateurs" :key="role.nom">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline mb-2">{{ role.nom }}</p>
                <div class="space-y-1.5">
                  <label v-for="u in role.utilisateurs" :key="u.id" class="flex items-center gap-2 cursor-pointer group">
                    <input type="checkbox" :value="u.id" v-model="form.estimation.utilisateur_ids"
                           class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                    <span class="text-sm text-on-surface group-hover:text-primary transition-colors">{{ u.nom_complet }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- ── 3. Personnel interne ── -->
          <div v-if="showSection('personnel')" id="section-personnel"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHeaderCls">
              <h2 :class="sectionTitleCls">Personnel</h2>
            </div>

            <!-- Mode détail : tableau estimation_personnels avec taux_horaire -->
            <template v-if="mod_detail">
              <div class="overflow-x-auto">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                      <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Personnel</th>
                      <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-28">Taux/h</th>
                      <th class="px-4 py-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-outline-variant/10">
                    <template v-for="(ep, i) in formDetailPersonnels" :key="i">
                      <tr v-if="!ep._destroy" class="hover:bg-surface-container-highest/10">
                        <td class="px-4 py-2">
                          <select v-model="ep.personnel_id" :class="inputSmCls">
                            <option value="">— Sélectionner —</option>
                            <option v-for="p in personnels" :key="p.id" :value="p.id">{{ p.nom_complet }}</option>
                          </select>
                        </td>
                        <td class="px-4 py-2"><input v-model="ep.taux_horaire" type="number" step="0.01" :class="inputSmCls" /></td>
                        <td class="px-4 py-2 text-center">
                          <button v-if="!ep.dependant" type="button" @click="removeDetailRow(formDetailPersonnels, i)" :class="removeBtnCls">
                            <span class="material-symbols-outlined text-base">delete</span>
                          </button>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
              <div class="px-6 py-3 border-t border-outline-variant/10">
                <button type="button" @click="addEstimationPersonnel" :class="addBtnCls">
                  <span class="material-symbols-outlined text-sm">add</span>Ajouter
                </button>
              </div>
            </template>

            <!-- Mode checkbox (par défaut) -->
            <div v-else class="p-6">
              <div v-if="activer_fonction" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                <div v-for="groupe in personnels_par_fonction" :key="groupe.code">
                  <p class="text-[10px] font-black uppercase tracking-widest text-outline mb-2">{{ groupe.code }}</p>
                  <div class="space-y-1.5">
                    <label v-for="p in groupe.personnels" :key="p.id" class="flex items-center gap-2 cursor-pointer group">
                      <input type="checkbox" :value="p.id" v-model="form.estimation.personnel_ids"
                             class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                      <span class="text-sm text-on-surface group-hover:text-primary transition-colors">{{ p.nom_complet }}</span>
                    </label>
                  </div>
                </div>
              </div>
              <div v-else class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                <label v-for="p in personnels" :key="p.id" class="flex items-center gap-2 cursor-pointer group">
                  <input type="checkbox" :value="p.id" v-model="form.estimation.personnel_ids"
                         class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                  <span class="text-sm text-on-surface group-hover:text-primary transition-colors">{{ p.nom_complet }}</span>
                </label>
              </div>
            </div>
          </div>

          <!-- ── 4. Personnel externe ── -->
          <div v-if="showSection('personnel_externe')" id="section-personnel_externe"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHeaderCls">
              <h2 :class="sectionTitleCls">
                Personnel externe
                <span class="text-outline/50">({{ form.estimation.personnel_externes_attributes.filter(r => !r._destroy).length }})</span>
              </h2>
              <button type="button" @click="addPersonnelExterne" :class="addBtnCls">
                <span class="material-symbols-outlined text-sm">add</span>Ajouter
              </button>
            </div>
            <div v-if="!form.estimation.personnel_externes_attributes.some(r => !r._destroy)"
                 class="px-6 py-8 text-center text-outline text-sm">
              <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">person_add</span>
              Aucun personnel externe
            </div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Code</th>
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Nom</th>
                    <th v-if="show_prenom" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Prénom</th>
                    <th v-if="show_type_fonction" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Profession</th>
                    <th v-if="show_description_perso" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Taux/h</th>
                    <th class="px-4 py-3 text-center text-[10px] font-black uppercase tracking-widest text-outline w-16">Actif</th>
                    <th class="px-4 py-3 w-12"></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-outline-variant/10">
                  <template v-for="(pe, i) in form.estimation.personnel_externes_attributes" :key="i">
                    <tr v-if="!pe._destroy" class="hover:bg-surface-container-highest/10">
                      <td class="px-4 py-2"><input v-model="pe.code" type="text" :class="inputSmCls" /></td>
                      <td class="px-4 py-2"><input v-model="pe.nom" type="text" :class="inputSmCls" /></td>
                      <td v-if="show_prenom" class="px-4 py-2"><input v-model="pe.prenom" type="text" :class="inputSmCls" /></td>
                      <td v-if="show_type_fonction" class="px-4 py-2">
                        <select v-model="pe.type_fonction_id" :class="inputSmCls">
                          <option value="">—</option>
                          <option v-for="f in type_fonctions" :key="f.id" :value="f.id">{{ f.label }}</option>
                        </select>
                      </td>
                      <td v-if="show_description_perso" class="px-4 py-2"><input v-model="pe.description" type="text" :class="inputSmCls" /></td>
                      <td class="px-4 py-2"><input v-model="pe.taux_horaire" type="number" step="0.01" :class="inputSmCls" /></td>
                      <td class="px-4 py-2 text-center">
                        <input type="checkbox" v-model="pe.actif" class="rounded border-outline-variant/40 text-primary" />
                      </td>
                      <td class="px-4 py-2 text-center">
                        <button type="button" @click="removeRow(form.estimation.personnel_externes_attributes, i)" :class="removeBtnCls">
                          <span class="material-symbols-outlined text-base">delete</span>
                        </button>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ── 5. Équipements internes ── -->
          <div v-if="showSection('equipements')" id="section-equipements"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHeaderCls">
              <h2 :class="sectionTitleCls">Équipements</h2>
            </div>

            <!-- Mode détail : tableau estimation_equipements avec taux_horaire -->
            <template v-if="mod_detail">
              <div class="overflow-x-auto">
                <table class="w-full text-sm border-collapse">
                  <thead>
                    <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                      <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Équipement</th>
                      <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-28">Taux/h</th>
                      <th class="px-4 py-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-outline-variant/10">
                    <template v-for="(ee, i) in formDetailEquipements" :key="i">
                      <tr v-if="!ee._destroy" class="hover:bg-surface-container-highest/10">
                        <td class="px-4 py-2">
                          <select v-model="ee.equipement_id" :class="inputSmCls">
                            <option value="">— Sélectionner —</option>
                            <option v-for="eq in equipements" :key="eq.id" :value="eq.id">{{ eq.nom }}</option>
                          </select>
                        </td>
                        <td class="px-4 py-2"><input v-model="ee.taux_horaire" type="number" step="0.01" :class="inputSmCls" /></td>
                        <td class="px-4 py-2 text-center">
                          <button v-if="!ee.dependant" type="button" @click="removeDetailRow(formDetailEquipements, i)" :class="removeBtnCls">
                            <span class="material-symbols-outlined text-base">delete</span>
                          </button>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
              <div class="px-6 py-3 border-t border-outline-variant/10">
                <button type="button" @click="addEstimationEquipement" :class="addBtnCls">
                  <span class="material-symbols-outlined text-sm">add</span>Ajouter
                </button>
              </div>
            </template>

            <!-- Mode checkbox (par défaut) -->
            <div v-else class="p-6">
              <div v-if="activer_type_equipement" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                <div v-for="groupe in equipements_par_type" :key="groupe.code">
                  <p class="text-[10px] font-black uppercase tracking-widest text-outline mb-2">{{ groupe.code }}</p>
                  <div class="space-y-1.5">
                    <label v-for="eq in groupe.equipements" :key="eq.id" class="flex items-center gap-2 cursor-pointer group">
                      <input type="checkbox" :value="eq.id" v-model="form.estimation.equipement_ids"
                             class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                      <span class="text-sm text-on-surface group-hover:text-primary transition-colors">{{ eq.nom }}</span>
                    </label>
                  </div>
                </div>
              </div>
              <div v-else class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                <label v-for="eq in equipements" :key="eq.id" class="flex items-center gap-2 cursor-pointer group">
                  <input type="checkbox" :value="eq.id" v-model="form.estimation.equipement_ids"
                         class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                  <span class="text-sm text-on-surface group-hover:text-primary transition-colors">{{ eq.nom }}</span>
                </label>
              </div>
            </div>
          </div>

          <!-- ── 6. Équipements externes ── -->
          <div v-if="showSection('equipement_externe')" id="section-equipement_externe"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHeaderCls">
              <h2 :class="sectionTitleCls">
                Équipements externes
                <span class="text-outline/50">({{ form.estimation.equipement_externes_attributes.filter(r => !r._destroy).length }})</span>
              </h2>
              <button type="button" @click="addEquipementExterne" :class="addBtnCls">
                <span class="material-symbols-outlined text-sm">add</span>Ajouter
              </button>
            </div>
            <div v-if="!form.estimation.equipement_externes_attributes.some(r => !r._destroy)"
                 class="px-6 py-8 text-center text-outline text-sm">
              <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">construction</span>
              Aucun équipement externe
            </div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Code</th>
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Nom</th>
                    <th v-if="show_type_equipement" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Type</th>
                    <th v-if="show_description_equip" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Taux/h</th>
                    <th class="px-4 py-3 text-center text-[10px] font-black uppercase tracking-widest text-outline w-16">Actif</th>
                    <th class="px-4 py-3 w-12"></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-outline-variant/10">
                  <template v-for="(ee, i) in form.estimation.equipement_externes_attributes" :key="i">
                    <tr v-if="!ee._destroy" class="hover:bg-surface-container-highest/10">
                      <td class="px-4 py-2"><input v-model="ee.code" type="text" :class="inputSmCls" /></td>
                      <td class="px-4 py-2"><input v-model="ee.nom" type="text" :class="inputSmCls" /></td>
                      <td v-if="show_type_equipement" class="px-4 py-2">
                        <select v-model="ee.type_equipement_id" :class="inputSmCls">
                          <option value="">—</option>
                          <option v-for="t in type_equipements" :key="t.id" :value="t.id">{{ t.label }}</option>
                        </select>
                      </td>
                      <td v-if="show_description_equip" class="px-4 py-2"><input v-model="ee.description" type="text" :class="inputSmCls" /></td>
                      <td class="px-4 py-2"><input v-model="ee.taux_horaire" type="number" step="0.01" :class="inputSmCls" /></td>
                      <td class="px-4 py-2 text-center">
                        <input type="checkbox" v-model="ee.actif" class="rounded border-outline-variant/40 text-primary" />
                      </td>
                      <td class="px-4 py-2 text-center">
                        <button type="button" @click="removeRow(form.estimation.equipement_externes_attributes, i)" :class="removeBtnCls">
                          <span class="material-symbols-outlined text-base">delete</span>
                        </button>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ── 7. Inventaires ── -->
          <div v-if="showSection('inventaires') && inventaire_enabled" id="section-inventaires"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHeaderCls">
              <h2 :class="sectionTitleCls">
                Inventaire
                <span class="text-outline/50">({{ form.estimation.estimation_inventaires_attributes.filter(r => !r._destroy).length }})</span>
              </h2>
              <button type="button" @click="addInventaire" :class="addBtnCls">
                <span class="material-symbols-outlined text-sm">add</span>Ajouter
              </button>
            </div>
            <div v-if="!form.estimation.estimation_inventaires_attributes.some(r => !r._destroy)"
                 class="px-6 py-8 text-center text-outline text-sm">
              <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">inventory_2</span>
              Aucun inventaire
            </div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Inventaire</th>
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-28">Taux</th>
                    <th v-if="show_type_taux" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-32">Type taux</th>
                    <th class="px-4 py-3 w-12"></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-outline-variant/10">
                  <template v-for="(inv, i) in form.estimation.estimation_inventaires_attributes" :key="i">
                    <tr v-if="!inv._destroy" class="hover:bg-surface-container-highest/10">
                      <td class="px-4 py-2">
                        <select v-model="inv.inventaire_id" :class="inputSmCls">
                          <option value="">— Sélectionner —</option>
                          <option v-for="inv2 in inventaires" :key="inv2.id" :value="inv2.id">{{ inv2.label }}</option>
                        </select>
                      </td>
                      <td class="px-4 py-2"><input v-model="inv.taux" type="number" step="0.01" :class="inputSmCls" /></td>
                      <td v-if="show_type_taux" class="px-4 py-2">
                        <select v-model="inv.type_taux_id" :class="inputSmCls">
                          <option v-for="tt in type_taux" :key="tt.id" :value="tt.id">{{ tt.code }}</option>
                        </select>
                      </td>
                      <td class="px-4 py-2 text-center">
                        <button type="button" @click="removeRow(form.estimation.estimation_inventaires_attributes, i)" :class="removeBtnCls">
                          <span class="material-symbols-outlined text-base">delete</span>
                        </button>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ── 8. Catégories d'items ── -->
          <div v-if="showSection('item_tags')" id="section-item_tags"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHeaderCls">
              <h2 :class="sectionTitleCls">
                Catégories d'items
                <span class="text-outline/50">({{ form.estimation.estimation_element_tags_attributes.filter(t => !t._destroy).length }})</span>
              </h2>
              <button type="button" @click="addTag" :class="addBtnCls">
                <span class="material-symbols-outlined text-sm">add</span>Ajouter
              </button>
            </div>
            <div v-if="!form.estimation.estimation_element_tags_attributes.some(t => !t._destroy)"
                 class="px-6 py-8 text-center text-outline text-sm">
              <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">label</span>
              Aucune catégorie
            </div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Nom de la catégorie</th>
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Ordre</th>
                    <th class="px-4 py-3 w-12"></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-outline-variant/10">
                  <template v-for="(tag, i) in form.estimation.estimation_element_tags_attributes" :key="i">
                    <tr v-if="!tag._destroy" class="hover:bg-surface-container-highest/10">
                      <td class="px-4 py-2"><input v-model="tag.tag" type="text" :class="inputSmCls" /></td>
                      <td class="px-4 py-2"><input v-model="tag.ordre" type="number" :class="inputSmCls" /></td>
                      <td class="px-4 py-2 text-center">
                        <button type="button" @click="removeRow(form.estimation.estimation_element_tags_attributes, i)" :class="removeBtnCls">
                          <span class="material-symbols-outlined text-base">delete</span>
                        </button>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ── 9. Items ── -->
          <div v-if="showSection('items')" id="section-items"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHeaderCls">
              <h2 :class="sectionTitleCls">
                Items
                <span class="text-outline/50">({{ form.estimation.estimation_elements_attributes.filter(e => !e._destroy).length }})</span>
              </h2>
              <button type="button" @click="addElement" :class="addBtnCls">
                <span class="material-symbols-outlined text-sm">add</span>Ajouter
              </button>
            </div>
            <div v-if="!form.estimation.estimation_elements_attributes.some(e => !e._destroy)"
                 class="px-6 py-8 text-center text-outline text-sm">
              <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">list_alt</span>
              Aucun item
            </div>
            <div v-else class="overflow-x-auto">
              <!-- OPTION A: deux rangées par item — ROLLBACK: remplacer par le bloc commenté ci-dessous -->
              <table class="w-full text-sm border-collapse table-fixed">
                <colgroup>
                  <col style="width: 80px">
                  <col v-if="tagsDisponibles.length > 0" style="width: 224px">
                  <col><!-- description : prend le reste -->
                  <col style="width: 96px">
                  <col style="width: 112px">
                  <col style="width: 48px">
                </colgroup>
                <tbody>
                  <template v-for="(el, i) in form.estimation.estimation_elements_attributes" :key="i">
                    <!-- Labels par item -->
                    <tr v-if="!el._destroy" class="border-t border-outline-variant/10 bg-surface-container-high/30">
                      <th class="px-4 pt-2 pb-0.5 text-left text-[9px] font-black uppercase tracking-widest text-outline/60 w-20">Code</th>
                      <th v-if="tagsDisponibles.length > 0" class="px-4 pt-2 pb-0.5 text-left text-[9px] font-black uppercase tracking-widest text-outline/60 w-56">Catégorie</th>
                      <th class="px-4 pt-2 pb-0.5 text-left text-[9px] font-black uppercase tracking-widest text-outline/60">Description</th>
                      <th class="px-4 pt-2 pb-0.5 text-left text-[9px] font-black uppercase tracking-widest text-outline/60 w-24">Qté</th>
                      <th class="px-4 pt-2 pb-0.5 text-left text-[9px] font-black uppercase tracking-widest text-outline/60 w-28">Unité</th>
                      <th class="px-4 pt-2 pb-0.5 w-12"></th>
                    </tr>
                    <!-- Rangée 1 : champs principaux -->
                    <tr v-if="!el._destroy" class="hover:bg-surface-container-highest/10 bg-surface-container-high/30">
                      <td class="px-4 pt-0.5 pb-2"><input v-model="el.code" type="text" :class="inputSmCls" /></td>
                      <td v-if="tagsDisponibles.length > 0" class="px-4 pt-0.5 pb-2">
                        <select v-model="el.estimation_element_tag_id" :class="inputSmCls">
                          <option value="">—</option>
                          <option v-for="tag in tagsDisponibles" :key="tag.id ?? tag.tag" :value="tag.id ?? tag.tag">
                            {{ tag.tag }}
                          </option>
                        </select>
                      </td>
                      <td class="px-4 pt-0.5 pb-2 relative">
                        <div class="relative">
                          <button type="button" @click.stop="openDescPopover(i, el.description)"
                                  class="w-full max-w-full text-left px-2 py-1.5 text-xs text-on-surface bg-surface-container-highest border border-outline-variant/20 rounded hover:ring-1 hover:ring-primary/30 transition-all overflow-hidden">
                            <span v-if="el.description" class="block truncate">{{ el.description }}</span>
                            <span v-else class="text-outline/40 italic">—</span>
                          </button>
                          <!-- Popover -->
                          <div v-if="expandedDescIndex === i" @click.stop
                               class="absolute top-full left-0 z-50 w-[560px] mt-1 bg-surface-container rounded-xl border border-outline-variant/20 shadow-2xl p-4">
                            <p class="text-[9px] font-black uppercase tracking-widest text-outline mb-2">Description</p>
                            <textarea :ref="setDescTextareaRef" v-model="expandedDescValue" rows="4"
                                      class="w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none transition-all"></textarea>
                            <div class="flex justify-end gap-2 mt-3">
                              <button type="button" @click="cancelDescPopover"
                                      class="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-highest border border-outline-variant/20 rounded-md transition-all">
                                Annuler
                              </button>
                              <button type="button" @click="validateDescPopover(el)"
                                      class="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-on-primary bg-primary rounded-md hover:brightness-110 transition-all">
                                Valider
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td class="px-4 pt-0.5 pb-2"><input v-model="el.quantite" type="number" step="0.01" :class="inputSmCls" /></td>
                      <td class="px-4 pt-0.5 pb-2">
                        <select v-model="el.type_unite_id" :class="inputSmCls">
                          <option value="">—</option>
                          <option v-for="u in type_unites" :key="u.id" :value="u.id">{{ u.code }}</option>
                        </select>
                      </td>
                      <td class="px-4 pt-0.5 pb-2 text-center">
                        <button type="button" @click="removeRow(form.estimation.estimation_elements_attributes, i)" :class="removeBtnCls">
                          <span class="material-symbols-outlined text-base">delete</span>
                        </button>
                      </td>
                    </tr>
                    <!-- Rangée 2 : coûts par partie -->
                    <tr v-if="!el._destroy && can_see_costs" class="hover:bg-surface-container-highest/10">
                      <td :colspan="tagsDisponibles.length > 0 ? 6 : 5" class="px-4 pb-3 pt-1">
                        <div class="flex flex-wrap gap-x-4 gap-y-1 pl-3 border-l-2 border-primary/20">
                          <div v-for="(ep, j) in el.estimation_parties_attributes" :key="j" class="flex flex-col gap-1 min-w-[90px]">
                            <span class="text-[9px] font-black uppercase tracking-widest text-outline/60">{{ type_parties[j]?.code }}</span>
                            <input v-model="ep.prix_coutant" type="number" step="0.01" :class="inputSmCls" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
              <!-- ROLLBACK: remplacer le bloc ci-dessus par celui-ci pour revenir à une rangée par item
              <table class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                    <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Code</th>
                    <th v-if="tagsDisponibles.length > 0" class="...">Catégorie</th>
                    <th class="...">Description</th>
                    <th class="... w-20">Qté</th>
                    <th class="... w-20">Unité</th>
                    [colonnes de coûts]
                    <th class="px-4 py-3 w-12"></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-outline-variant/10">
                  <template v-for="(el, i) in form.estimation.estimation_elements_attributes" :key="i">
                    <tr v-if="!el._destroy" class="hover:bg-surface-container-highest/10">
                      [toutes les td sur une seule ligne incluant les coûts]
                    </tr>
                  </template>
                </tbody>
              </table>
              -->
            </div>
          </div>

          <!-- ── 10. Parties suivies ── -->
          <div v-if="showSection('partie_suivies') && type_partie_suivies.length > 0" id="section-partie_suivies"
               class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
            <div :class="sectionHeaderCls">
              <h2 :class="sectionTitleCls">
                Parties suivies
                <span class="text-outline/50">({{ form.estimation.estimation_partie_suivies_attributes.filter(p => !p._destroy).length }})</span>
              </h2>
              <button type="button" @click="addPartieSuivie" :class="addBtnCls">
                <span class="material-symbols-outlined text-sm">add</span>Ajouter
              </button>
            </div>
            <div class="p-6 space-y-3">
              <template v-for="(ps, i) in form.estimation.estimation_partie_suivies_attributes" :key="i">
                <div v-if="!ps._destroy" class="flex items-center gap-3">
                  <select v-model="ps.type_partie_estimation_id" :class="inputCls + ' flex-1'">
                    <option value="">— Sélectionner —</option>
                    <option v-for="tp in type_partie_suivies" :key="tp.id" :value="tp.id">{{ tp.code }}</option>
                  </select>
                  <button type="button" @click="removeRow(form.estimation.estimation_partie_suivies_attributes, i)" :class="removeBtnCls">
                    <span class="material-symbols-outlined text-base">delete</span>
                  </button>
                </div>
              </template>
              <div v-if="!form.estimation.estimation_partie_suivies_attributes.some(p => !p._destroy)"
                   class="text-sm text-outline text-center py-4">Aucune partie suivie sélectionnée</div>
            </div>
          </div>

          <!-- ── Actions ── -->
          <div class="flex items-center justify-between pt-2 pb-8">
            <a :href="estimation_url"
               class="px-5 py-2.5 rounded-md text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container border border-outline-variant/20 hover:bg-surface-container-highest transition-all">
              Annuler
            </a>
            <button ref="bottomSaveBtn" type="submit" :disabled="form.processing"
                    class="machined-sheen px-8 py-2.5 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
              <span v-if="form.processing" class="material-symbols-outlined text-sm animate-spin">progress_activity</span>
              <span v-else class="material-symbols-outlined text-sm">save</span>
              Enregistrer les modifications
            </button>
          </div>

        </form>

      </div>
    </div>

    <!-- Overlay transparent pour fermer le popover description -->
    <div v-if="expandedDescIndex !== null"
         @click="cancelDescPopover"
         class="fixed inset-0 z-40"></div>

    <!-- ── Boutons flottants ── -->
    <div class="fixed bottom-[39px] right-6 z-50 flex flex-row items-center gap-2">

      <!-- Bouton Enregistrer : visible uniquement si le formulaire est modifié -->
      <Transition
        enter-active-class="transition-all duration-200"
        enter-from-class="opacity-0 translate-y-3 scale-90"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="transition-all duration-150"
        leave-from-class="opacity-100 translate-y-0 scale-100"
        leave-to-class="opacity-0 translate-y-3 scale-90">
        <button v-if="form.isDirty && !bottomSaveInView" type="button" @click="submit" :disabled="form.processing"
                class="machined-sheen flex items-center gap-2 px-4 py-2.5 rounded-full text-on-primary font-black text-[10px] uppercase tracking-widest shadow-xl hover:brightness-110 active:scale-95 transition-all disabled:opacity-50">
          <span v-if="form.processing" class="material-symbols-outlined text-sm animate-spin">progress_activity</span>
          <span v-else class="material-symbols-outlined text-sm">save</span>
          Enregistrer
        </button>
      </Transition>

      <!-- Bouton ↑ : visible après 300px de scroll -->
      <Transition
        enter-active-class="transition-all duration-200"
        enter-from-class="opacity-0 translate-y-3 scale-90"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="transition-all duration-150"
        leave-from-class="opacity-100 translate-y-0 scale-100"
        leave-to-class="opacity-0 translate-y-3 scale-90">
        <button v-if="showScrollTop" type="button" @click="scrollToTop"
                class="w-10 h-10 rounded-full bg-surface-container border border-outline-variant/20 shadow-lg flex items-center justify-center text-outline hover:text-on-surface hover:bg-surface-container-highest active:scale-95 transition-all">
          <span class="material-symbols-outlined text-base">arrow_upward</span>
        </button>
      </Transition>

    </div>

  </AppLayout>
</template>
